import { Component, ViewChild } from '@angular/core';
import { getSegmentName, translateSegments } from '@shared-libs/helpers';
import { Segment } from '@beego/ngx-segment';
import { Action, NavigationDataKey, NavigationType, UserPermissions } from '@shared-libs/enums';
import { NavigationService } from '@shared-services/navigation.service';
import { MessageService } from '@shared-services/message.service';
import { IDataQuery, ITableData, TableDesktopComponent } from '@beego/ngx-table';
import { DeviceProvider } from '@internal-providers/device.provider';
import { DeviceStatus, IDevice } from '@internal-models/device.model';
import { DeviceComponent } from '@internal-components/device-components/device-component/device.component';
import { LoanComponent } from '@internal-components/device-components/loan-component/loan.component';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import { DeviceTableFactory } from '@internal-factories/tables/devices-table.factory';
import { ImportDevicesComponent } from '@internal-components/device-components/import-devices-component/import-devices.component';
import { ILoan } from '@internal-models/loan.model';
import { LoanProvider } from '@internal-providers/loan.provider';
import { ValidationService } from '@shared-services/validation.service';
import { ReturnLoanComponent } from '@internal-components/device-components/return-loan-component/return-loan.component';

@Component({
	selector: 'devices-desktop-page',
	templateUrl: 'devices.desktop.page.html',
	styleUrls: ['./devices.desktop.page.scss'],
})
export class DevicesDesktopPage {
	@ViewChild(TableDesktopComponent) private readonly tableComponent: TableDesktopComponent;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;
	public readonly DeviceStatus: typeof DeviceStatus = DeviceStatus;
	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly NavigationDataKey: typeof NavigationDataKey = NavigationDataKey;
	public readonly tableHeaders = DeviceTableFactory.getHeaders();
	public readonly tableFilters = DeviceTableFactory.getFilters();
	public readonly loanTableFilters = DeviceTableFactory.getLoanFilters();
	public readonly segments: Array<Segment> = translateSegments([
		'CATALOGUE',
		'IN_STOCK',
		'IN_LOAN',
		'RETURNED',
		'IN_REFURBISH',
		'DISABLED',
	]);
	public selectedSegment: string = getSegmentName(this.segments[1]);
	public getSegmentName = getSegmentName;

	public allDevices: Array<IDevice> = new Array();
	public inStockDevices: Array<IDevice> = new Array();
	public loans: Array<ILoan> = new Array();
	public returnedDevices: Array<IDevice> = new Array();
	public inRefurbishDevices: Array<IDevice> = new Array();
	public disabledDevices: Array<IDevice> = new Array();

	public selectedDevices: Array<IDevice> = new Array();
	public showBulkActions: boolean = false;

	constructor(
		private readonly provider: DeviceProvider,
		private readonly loanProvider: LoanProvider,
		private readonly navigationService: NavigationService,
		private readonly messageService: MessageService,
		private readonly validationService: ValidationService
	) {
		this.navigationService.onNavigateToCurrentPage().subscribe((navigation) => {
			this.tableComponent?.refreshData();
			if (navigation?.closing?.data.action) {
				this.messageService.showSavedMessage();
			}
		});
	}

	public onSelectedSegmentChange(segment: string): void {
		this.selectedSegment = segment;
	}

	public readonly allDevicesDataRequest = (query: IDataQuery) => this.getAllDevices(query);
	public readonly inStockDataRequest = (query: IDataQuery) => this.getInStockDevices(query);
	public readonly inLoanDataRequest = (query: IDataQuery) => this.getActiveLoans(query);
	public readonly returnedDataRequest = (query: IDataQuery) => this.getReturnedDevices(query);
	public readonly inRefurbishDataRequest = (query: IDataQuery) => this.getInRefurbishDevices(query);
	public readonly disabledDataRequest = (query: IDataQuery) => this.getDisabledDevices(query);

	public dataChanged(devices: Array<IDevice | ILoan>): void {
		switch (this.selectedSegment) {
			case getSegmentName(this.segments[0]):
				this.allDevices = devices as Array<IDevice>;
				break;
			case getSegmentName(this.segments[1]):
				this.inStockDevices = devices as Array<IDevice>;
				break;
			case getSegmentName(this.segments[2]):
				this.loans = devices as Array<ILoan>;
				break;
			case getSegmentName(this.segments[3]):
				this.returnedDevices = devices as Array<IDevice>;
				break;
			case getSegmentName(this.segments[4]):
				this.inRefurbishDevices = devices as Array<IDevice>;
				break;
			case getSegmentName(this.segments[5]):
				this.disabledDevices = devices as Array<IDevice>;
				break;
		}
		this.resetBulkActions();
	}

	public async loanDevice(device?: IDevice): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
			devices: device ? [device] : this.selectedDevices ?? [],
			loan: device?.Loans[0],
		});
	}

	public async openDevice(device?: IDevice): Promise<void> {
		if (device) {
			device = await this.provider.getOne(device.id);
		}
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, DeviceComponent, {
			device,
			action: device ? Action.UPDATE : Action.CREATE,
		});
	}

	public async editLoan(loan?: ILoan): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, LoanComponent, {
			loan,
			action: Action.UPDATE,
		});
	}

	public async endLoan(loan?: ILoan): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, ReturnLoanComponent, {
			loan,
		});
	}

	public async uploadDevices(): Promise<void> {
		await this.navigationService.navigateTo<{ action: Action }>(NavigationType.modal, ImportDevicesComponent);
	}

	public toggleSelection(device: IDevice) {
		const index = this.selectedDevices.indexOf(device);
		if (index > -1) {
			this.selectedDevices.splice(index, 1);
		} else {
			this.selectedDevices.push(device);
		}
		if (this.selectedDevices.length === 0) {
			this.showBulkActions = false;
		}
	}

	public toggleBulkActions() {
		this.showBulkActions = !this.showBulkActions;
	}

	public async updateDevicesStatus(status: DeviceStatus) {
		await this.provider.batchUpdateStatus(
			this.selectedDevices.map((device) => device.id),
			status
		);
		this.resetBulkActions();
		this.tableComponent?.refreshData();
	}

	public async deleteDevices() {
		if (await this.validationService.actionIsConfirmed('DEVICE')) {
			await this.provider.batchDelete(this.selectedDevices.map((device) => device.id));
			this.resetBulkActions();
			this.tableComponent?.refreshData();
		}
	}

	private resetBulkActions(): void {
		this.showBulkActions = false;
		this.selectedDevices = [];
	}

	private getAllDevices(query: IDataQuery): Promise<ITableData<IDevice>> {
		query.filter = { ...query.filter, isActive: [true] };
		return this.provider.getAll(query);
	}

	private getInStockDevices(query: IDataQuery): Promise<ITableData<IDevice>> {
		query.filter = { ...query.filter, status: DeviceStatus.InStock, isActive: [true] };
		return this.provider.getAll(query);
	}

	private getActiveLoans(query: IDataQuery): Promise<ITableData<ILoan>> {
		query.filter = { ...query.filter };
		return this.loanProvider.getAll(query);
	}

	private getReturnedDevices(query: IDataQuery): Promise<ITableData<IDevice>> {
		query.filter = { ...query.filter, status: DeviceStatus.Returned, isActive: [true] };
		return this.provider.getAll(query);
	}

	private getInRefurbishDevices(query: IDataQuery): Promise<ITableData<IDevice>> {
		query.filter = { ...query.filter, status: DeviceStatus.InRefurbish, isActive: [true] };
		return this.provider.getAll(query);
	}

	private getDisabledDevices(query: IDataQuery): Promise<ITableData<IDevice>> {
		query.filter = { ...query.filter, status: DeviceStatus.Disabled, isActive: [true] };
		return this.provider.getAll(query);
	}
}
